import { css } from '@emotion/css';
import { type AriaRole, type ReactNode } from 'react';
import * as React from 'react';

import { type GrafanaTheme2 } from '@grafana/data';

import { useStyles2 } from '../../themes/ThemeContext';
import { Box } from '../Layout/Box/Box';
import { Stack } from '../Layout/Stack/Stack';
import { Text } from '../Text/Text';

import { GrotCTA } from './GrotCTA/GrotCTA';
import { GrotCompleted } from './GrotCompleted/GrotCompleted';
import { GrotNotFound } from './GrotNotFound/GrotNotFound';

interface Props {
  /**
   * Provide a button to render below the message
   */
  button?: ReactNode;
  hideImage?: boolean;
  /**
   * Override the default image for the variant
   */
  image?: ReactNode;
  /**
   * Message to display to the user. Strings use the `h4` text style; React nodes render as provided.
   */
  message: ReactNode;
  /**
   * Which variant to use. Affects the default image shown.
   */
  variant: 'call-to-action' | 'not-found' | 'completed';
  /**
   * Use to set `alert` when needed. See documentation for the use case
   */
  role?: AriaRole;
}

/**
 * The EmptyState component consists of a message and optionally an image, button, and additional information.
 *
 * https://developers.grafana.com/ui/latest/index.html?path=/docs/information-emptystate--docs
 */
export const EmptyState = ({
  button,
  children,
  image,
  message,
  hideImage = false,
  variant,
  role,
}: React.PropsWithChildren<Props>) => {
  const styles = useStyles2(getStyles);
  const imageToShow = image ?? getDefaultImageForVariant(variant, styles.image);

  return (
    <Box paddingY={4} display="flex" direction="column" alignItems="center" role={role}>
      <div className={styles.container}>
        {!hideImage && imageToShow}
        <Stack direction="column" alignItems="center">
          {typeof message === 'string' ? (
            <Text variant="h4" textAlignment="center">
              {message}
            </Text>
          ) : (
            message
          )}
          {children && (
            <Text color="secondary" textAlignment="center">
              {children}
            </Text>
          )}
        </Stack>
        {button}
      </div>
    </Box>
  );
};

function getDefaultImageForVariant(variant: Props['variant'], className?: string) {
  switch (variant) {
    case 'call-to-action': {
      return <GrotCTA width={300} className={className} />;
    }
    case 'not-found': {
      return <GrotNotFound width={300} className={className} />;
    }
    case 'completed': {
      return <GrotCompleted width={300} className={className} />;
    }
    default: {
      throw new Error(`Unknown variant: ${variant}`);
    }
  }
}

const getStyles = (theme: GrafanaTheme2) => ({
  container: css({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(4),
    maxWidth: '600px',
  }),
  image: css({
    maxWidth: '100%',
  }),
});
